import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    Check,
    Combo,
    Pagination,
    Result,
    Spin,
    Typography,
    DownOutlined
} from '@fx-ui/fine-design';
import {
    every,
    get,
    isEmpty,
    map,
    size,
    some,
    formatRegionDatetime,
    parseDate,
    SuiteId
} from '@libs/shared';
import classNames from 'classnames';
import emptyImg from '../../../../assets/empty/message-empty.png';
import { Hyperlink } from '@fx-ui/jdy-design-biz';
import { MessageFilterItem, MessageFilterType, MessageNavType } from '../../interfaces';
import { getRequestUrl, post, tracker } from '../../../../utils';
import { authModel } from '../../../../models';
import { platformIntl, API, TRACKER } from '../../../../constants';

interface IMessageList {
    type: MessageNavType; // 通知类型
    limit?: number; // 每页通知条数
    hasSetRead?: boolean; // 是否可设置已读
    expireTip?: string; // 保存数据时间提示
    isShowLink?: boolean; // 是否显示消息详情中的链接
    currentSuiteId?: SuiteId;
    messageFilters: MessageFilterItem[]; // 消息列表类型筛选项
}

/**
 * 格式化消息内容，主要是确保之前的换行效果保留下来
 * @param message 消息内容
 */
const formatMessage = (message: string | string[]) => {
    if (Array.isArray(message)) {
        /**
         * 一个消息里有多条消息文案时，在每个文案直接增加换行元素 br 以保持换行效果
         * 注意，这里直接用 join('\n') 的话，会看不到换行效果
         */
        return map(message, (text, index) => {
            const isLastMessage = index === message.length - 1;
            return (
                <Fragment key={index}>
                    {text}
                    {!isLastMessage && <br />}
                </Fragment>
            );
        });
    }
    return message;
};

/**
 * 消息列表
 *
 * @param props IMessageList
 * @class
 */
const MessageList: React.FC<IMessageList> = (props) => {
    const timezone = authModel.timezone;
    const region = authModel.region;
    const {
        type,
        limit = 10,
        hasSetRead = true,
        expireTip = '',
        isShowLink = true,
        messageFilters,
        currentSuiteId
    } = props;

    const [messageType, setMessageType] = useState<MessageNavType | MessageFilterType>(type);
    const [messageList, setMessageList] = useState<Record<string, any>[]>([]);
    const [isReadAll, setIsReadAll] = useState<boolean>(true);
    const [loading, setLoading] = useState(true);

    const pageRef = useRef({
        page: 1,
        count: 0
    });
    const isLastPage = useMemo(() => {
        return pageRef.current.count <= pageRef.current.page * limit;
    }, [pageRef.current.count, pageRef.current.page, limit]);

    /**
     * 获取消息列表请求
     */
    const getMessageList = () => {
        setLoading(true);
        const data = {
            isRead: isReadAll ? undefined : false,
            skip: (pageRef.current.page - 1) * limit,
            limit: limit,
            type: messageType,
            ...(currentSuiteId ? { suite_id: currentSuiteId } : {})
        };
        post(getRequestUrl(API.message.list), data)
            .then((res) => {
                const data = get(res, 'data', {});
                setMessageList(data.messages || []);
                pageRef.current.count = data.count || 0;
            })
            .finally(() => {
                setLoading(false);
            });
    };

    /**
     * 设置消息为已读
     *
     * @param id all或者消息id
     */
    const setReadMessage = (id: string) => {
        let ids: string[] = [];
        let messages = messageList;

        if (id === 'all') {
            ids = map(messageList, '_id');
            messages = map(messageList, (message) => ({
                ...message,
                isRead: true
            }));
        } else {
            const hasMsg = some(messageList, (item) => item._id === id);
            if (hasMsg) {
                ids.push(id);
                messages = map(messageList, (message) => {
                    return message._id === id
                        ? {
                              ...message,
                              isRead: true
                          }
                        : message;
                });
            }
        }

        if (messages !== messageList) {
            post(getRequestUrl(API.message.set_as_read), { messages: ids }).then(() => {
                setMessageList(messages);
            });
        }
    };

    useEffect(() => {
        pageRef.current.page = 1;
        getMessageList();
    }, [isReadAll, messageType]);

    // 日志范围选择下拉数据列表
    const rangeList = useMemo(() => {
        return [
            {
                text: platformIntl.get('all'),
                value: type
            },
            ...map(messageFilters, (item) => {
                return {
                    text: item.label,
                    value: item.key
                };
            })
        ];
    }, [messageFilters]);

    /**
     * 获取消息内容
     */
    const getMessageListContent = () => {
        if (isEmpty(messageList)) {
            return (
                <Result
                    className="message-empty"
                    description={platformIntl.get('message.empty')}
                    image={emptyImg}
                    imageStyle={{ height: 140 }}
                />
            );
        }
        return (
            <>
                <ul>
                    {map(messageList, (message: Record<string, any>) => {
                        return (
                            <li
                                className={classNames('message-item', {
                                    'style-grey': message.isRead
                                })}
                                key={message._id}
                            >
                                <div className="message-item-header">
                                    <div
                                        className={classNames('unread-point', {
                                            'has-read': message.isRead
                                        })}
                                    />
                                    <span className="message-time">
                                        {formatRegionDatetime({
                                            value: parseDate(message.sendTime, timezone),
                                            region,
                                            formatType: 'ymd_hm'
                                        })}
                                    </span>
                                    {hasSetRead && !message.isRead && (
                                        <span
                                            className="read-set-btn text-btn"
                                            onClick={() => {
                                                setReadMessage(message._id);
                                            }}
                                        >
                                            {platformIntl.get('msg.read')}
                                        </span>
                                    )}
                                </div>
                                <div className="message-item-body">
                                    {message.subject && (
                                        <div className="message-custom-title">
                                            {message.subject}
                                        </div>
                                    )}
                                    <Typography.Paragraph
                                        className={classNames('message-paragraph', {
                                            'has-read': message.isRead
                                        })}
                                        ellipsis={{
                                            rows: 4,
                                            expandable: true,
                                            collapsible: true,
                                            isExpandAlwaysRight: true,
                                            suffix: isShowLink &&
                                                message.link &&
                                                message.linkText && (
                                                    <Hyperlink
                                                        className="fx-message-link-btn"
                                                        href={message.link}
                                                        onClick={() => {
                                                            setReadMessage(message._id);
                                                        }}
                                                    >
                                                        {message.linkText}
                                                    </Hyperlink>
                                                ),
                                            symbol: (expanded) => {
                                                return (
                                                    <Button
                                                        className="message-expand"
                                                        icon={
                                                            <DownOutlined
                                                                className={classNames(
                                                                    'toggler-icon',
                                                                    { expanded }
                                                                )}
                                                            />
                                                        }
                                                        size="small"
                                                        type="negative"
                                                    >
                                                        {expanded
                                                            ? platformIntl.get('collapse')
                                                            : platformIntl.get('sidebar.unfold')}
                                                    </Button>
                                                );
                                            }
                                        }}
                                    >
                                        {formatMessage(message.content)}
                                    </Typography.Paragraph>
                                </div>
                            </li>
                        );
                    })}
                </ul>
                {!isEmpty(messageList) && isLastPage && expireTip && (
                    <div className="expire-tip">{expireTip}</div>
                )}
            </>
        );
    };

    const trackMessageType = (messageType: MessageFilterType) => {
        // 当选择【全部】时，埋点参数为 "all"
        const trackParam = !map(messageFilters, 'key').includes(messageType) ? 'all' : messageType;
        switch (type) {
            case MessageNavType.DataNotify:
                tracker(TRACKER.MESSAGE_CENTER_DATA_REMINDER_TAB_CLICK, trackParam);
                break;
            case MessageNavType.AppLog:
                tracker(TRACKER.MESSAGE_CENTER_APP_LOG_TAB_CLICK, trackParam);
                break;
            case MessageNavType.KmsDoc:
                tracker(TRACKER.MESSAGE_CENTER_DOC_DYNAMIC_TAB_CLICK, trackParam);
                break;
            case MessageNavType.OperationNotify:
                tracker(TRACKER.MESSAGE_CENTER_OPERATION_NOTIFY_TAB_CLICK, trackParam);
                break;
            case MessageNavType.TenantCoop:
                tracker(TRACKER.MESSAGE_CENTER_CONTACTS_MGMT_TAB_CLICK, trackParam);
                break;
            case MessageNavType.SystemManagement:
                tracker(TRACKER.MESSAGE_CENTER_SYS_MGMT_TAB_CLICK, trackParam);
                break;
            default:
                break;
        }
    };

    return (
        <div className="fx-message-list">
            <div className="message-list-header">
                <Combo
                    className={classNames('message-filter', {
                        hide: size(rangeList) <= 1 || authModel.isISVEncrypted
                    })}
                    value={messageType}
                    onChange={(value) => {
                        setMessageType(value as MessageFilterType);
                        trackMessageType(value as MessageFilterType);
                    }}
                    hasClear={false}
                    hasSearch={false}
                    hasBorder={false}
                    items={rangeList}
                />
                {hasSetRead && (
                    <div className="read-operation-btn">
                        <Check
                            value="a"
                            checked={!isReadAll}
                            onChange={(value: any) => {
                                setIsReadAll(!value);
                            }}
                            tabFocus
                        >
                            {platformIntl.get('message.unread')}
                        </Check>
                        <Button
                            type="text"
                            compact
                            onClick={() => {
                                setReadMessage('all');
                            }}
                        >
                            {platformIntl.get('msg.readAll')}
                        </Button>
                    </div>
                )}
            </div>
            <div className="message-list-body">{loading ? <Spin /> : getMessageListContent()}</div>
            {!isEmpty(messageList) && (
                <div className="message-list-footer">
                    <Pagination
                        hasTurnLast
                        page={pageRef.current.page}
                        showQuickJumper={false}
                        onPageChange={(page: number) => {
                            // 勾选只看未读时，如果当前页中的消息被全部置为已读，则翻到下一页时，分页参数应当保持不变
                            const isTurnToNextPage = page > pageRef.current.page;
                            const shouldKeepPage =
                                !isReadAll && isTurnToNextPage && every(messageList, 'isRead');
                            if (!shouldKeepPage) {
                                pageRef.current.page = page;
                            }
                            getMessageList();
                        }}
                        isLastPage={isLastPage}
                    />
                </div>
            )}
        </div>
    );
};

export default MessageList;
